import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

// assets
import SearchOutlined from '@ant-design/icons/SearchOutlined';

// project imports
import useRules from 'hooks/useRules';
import { listProducts } from 'api/products';
import { listOrders } from 'api/orders';
import { listCompanies, listPeople } from 'api/customers';
import { CatalogProduct } from 'types/products';
import { Order } from 'types/orders';
import { Customer } from 'types/customers';

type ResultItem = { id: string; title: string; subtitle?: string; onSelect: () => void };
type ResultGroup = { key: string; label: string; items: ResultItem[] };

const LOGISTICS_STATUSES: string[] = ['picking', 'partially_invoiced', 'invoiced', 'shipped'];
const LOGISTICS_STATUS_LABEL: Record<string, string> = {
  picking: 'Em separação',
  partially_invoiced: 'Parcialmente faturado',
  invoiced: 'Separado',
  shipped: 'Enviado'
};
const MAX_PER_GROUP = 5;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 350;

// ==============================|| HEADER CONTENT - SEARCH ||============================== //

/**
 * Busca global (produtos/pedidos/clientes). Só dispara buscas nas categorias em que o
 * usuário tem a rule de leitura correspondente — e mesmo assim os endpoints usados
 * (listOrders/listCompanies/listPeople) já aplicam o escopo por cliente no backend
 * (getCustomerOrderScope/getCustomerScope), então um usuário "Cliente" só vê o que é dele.
 */
export default function Search({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { hasRule, isAdmin } = useRules();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<ResultGroup[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef(0);

  const canSearchProducts = isAdmin || hasRule('products.read');
  const canSearchOrders = isAdmin || hasRule('orders.read');
  const canSearchCustomers = isAdmin || hasRule('customers.read');

  const goTo = (path: string) => {
    navigate(path);
    setOpen(false);
    onNavigate?.();
  };

  useEffect(() => {
    const term = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (term.length < MIN_QUERY_LENGTH) {
      setGroups([]);
      setLoading(false);
      setOpen(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setOpen(true);

    debounceRef.current = setTimeout(async () => {
      const termLower = term.toLowerCase();
      const nextGroups: ResultGroup[] = [];

      const tasks: Promise<void>[] = [];

      if (canSearchProducts) {
        tasks.push(
          listProducts()
            .then((products) => {
              const matches = (products || []).filter((p: CatalogProduct) =>
                [p.preferenceCode, p.code, p.newCode, p.description, p.color]
                  .filter(Boolean)
                  .some((f) => (f as string).toLowerCase().includes(termLower))
              );
              if (matches.length) {
                nextGroups.push({
                  key: 'products',
                  label: 'Produtos',
                  items: matches.slice(0, MAX_PER_GROUP).map((p) => ({
                    id: p.id,
                    title: p.description || p.preferenceCode || p.code || p.id,
                    subtitle: p.preferenceCode || p.code || undefined,
                    onSelect: () => goTo(`/products?q=${encodeURIComponent(term)}`)
                  }))
                });
              }
            })
            .catch(() => undefined)
        );
      }

      if (canSearchOrders) {
        tasks.push(
          listOrders()
            .then((res) => {
              const matches = (res.data || []).filter(
                (o: Order) =>
                  o.number?.toLowerCase().includes(termLower) ||
                  o.client?.name?.toLowerCase().includes(termLower) ||
                  o.client?.cnpj?.toLowerCase().includes(termLower)
              );
              if (matches.length) {
                nextGroups.push({
                  key: 'orders',
                  label: 'Pedidos',
                  items: matches.slice(0, MAX_PER_GROUP).map((o) => ({
                    id: o.id,
                    title: `#${o.number}`,
                    subtitle: o.client?.name || undefined,
                    onSelect: () => goTo(`/orders?q=${encodeURIComponent(term)}`)
                  }))
                });
              }

              // Pedidos que estão na Logística (separação, parcialmente faturado ou faturado): abre direto nela.
              const inLogistics = matches.filter((o: Order) => LOGISTICS_STATUSES.includes(o.status));
              if (inLogistics.length) {
                nextGroups.push({
                  key: 'logistics',
                  label: 'Logística',
                  items: inLogistics.slice(0, MAX_PER_GROUP).map((o) => ({
                    id: o.id,
                    title: `#${o.number}`,
                    subtitle: [o.client?.name, LOGISTICS_STATUS_LABEL[o.status]].filter(Boolean).join(' · ') || undefined,
                    onSelect: () => goTo(`/logistics?order=${encodeURIComponent(o.id)}`)
                  }))
                });
              }
            })
            .catch(() => undefined)
        );
      }

      if (canSearchCustomers) {
        tasks.push(
          Promise.all([listCompanies(term), listPeople(term)])
            .then(([companies, people]) => {
              const matches = [...(companies || []), ...(people || [])] as Customer[];
              if (matches.length) {
                nextGroups.push({
                  key: 'customers',
                  label: 'Clientes',
                  items: matches.slice(0, MAX_PER_GROUP).map((c) => ({
                    id: c.id,
                    title: c.displayName,
                    onSelect: () => goTo(`/clients?q=${encodeURIComponent(term)}`)
                  }))
                });
              }
            })
            .catch(() => undefined)
        );
      }

      await Promise.all(tasks);

      // ignora resposta de uma busca antiga (usuário já digitou algo novo)
      if (requestId !== requestIdRef.current) return;

      // mantém uma ordem estável de categorias
      const order = ['products', 'orders', 'logistics', 'customers'];
      nextGroups.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

      setGroups(nextGroups);
      setLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, canSearchProducts, canSearchOrders, canSearchCustomers]);

  const hasResults = groups.some((g) => g.items.length > 0);
  const showDropdown = open && query.trim().length >= MIN_QUERY_LENGTH;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ width: '100%', ml: { xs: 0, md: 1 }, position: 'relative' }}>
        <FormControl sx={{ width: { xs: '100%', md: 224 } }}>
          <OutlinedInput
            size="small"
            id="header-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (query.trim().length >= MIN_QUERY_LENGTH) setOpen(true);
            }}
            startAdornment={
              <InputAdornment position="start" sx={{ mr: -0.5 }}>
                <SearchOutlined />
              </InputAdornment>
            }
            aria-describedby="header-search-text"
            slotProps={{ input: { 'aria-label': 'Buscar produtos, pedidos ou clientes' } }}
            placeholder="Buscar..."
          />
        </FormControl>

        {showDropdown && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: (theme) => theme.zIndex.modal,
              maxHeight: 360,
              overflowY: 'auto',
              boxShadow: (theme) => theme.customShadows?.z1
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : hasResults ? (
              <List dense disablePadding>
                {groups.map((group) => (
                  <Box key={group.key}>
                    <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent', fontWeight: 700 }}>{group.label}</ListSubheader>
                    {group.items.map((item) => (
                      <ListItemButton key={item.id} onClick={item.onSelect}>
                        <ListItemText primary={item.title} secondary={item.subtitle} />
                      </ListItemButton>
                    ))}
                  </Box>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum resultado para &quot;{query}&quot;
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}
